import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

function PaymentSettings({ practiceId }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const loadSettings = useCallback(async () => {
    if (!practiceId) return;
    try {
      setLoading(true);
      const result = await api.get(`/practices/${practiceId}/payments/settings`);
      if (result.success) setSettings(result.data);
    } catch (err) {
      console.error('Error loading settings:', err);
    } finally {
      setLoading(false);
    }
  }, [practiceId]);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const handleSave = async () => {
    try {
      setSaving(true);
      const result = await api.put(`/practices/${practiceId}/payments/settings`, settings);
      if (result.success) {
        setSettings(result.data);
        setMessage('Settings saved successfully');
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (err) {
      setMessage('Error saving settings: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setSettings(s => ({ ...s, [field]: value }));
  };

  if (loading || !settings) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}><p>Loading settings...</p></div>;
  }

  return (
    <div style={{ padding: '1.5rem' }}>
      <h1 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: '1.5rem' }}>Payment Settings</h1>

      {message && (
        <div style={{ padding: '0.75rem', borderRadius: '6px', marginBottom: '1rem',
          background: message.includes('Error') ? '#FEE2E2' : '#ECFDF5',
          color: message.includes('Error') ? '#DC2626' : '#059669' }}>
          {message}
        </div>
      )}

      {/* General */}
      <Section title="General">
        <Field label="Payment Gateway" value={settings.payment_gateway}
          type="select" options={['STRIPE', 'AUTHORIZE_NET', 'SQUARE', 'EHR_NATIVE', 'ELAVON']}
          onChange={v => updateField('payment_gateway', v)} />
        <Field label="Payment Link Domain" value={settings.payment_link_domain}
          onChange={v => updateField('payment_link_domain', v)} />
        <Field label="Link Expiry (days)" value={settings.payment_link_expiry_days} type="number"
          onChange={v => updateField('payment_link_expiry_days', parseInt(v))} />
        <ToggleField label="Allow Partial Payments" value={settings.allow_partial_payments}
          onChange={v => updateField('allow_partial_payments', v)} />
        <Field label="Minimum Payment %" value={settings.minimum_payment_percentage} type="number"
          onChange={v => updateField('minimum_payment_percentage', parseFloat(v))} />
      </Section>

      {/* Stripe Connect */}
      {settings.payment_gateway === 'STRIPE' && (
        <Section title="Stripe Configuration">
          <Field label="Stripe Account ID" value={settings.gateway_account_id}
            onChange={v => updateField('gateway_account_id', v)} hint="acct_xxxx — from your Stripe Dashboard → Settings → Account" />
          <Field label="Stripe Secret Key" value={settings.gateway_api_key_id}
            onChange={v => updateField('gateway_api_key_id', v)} hint="sk_test_xxxx (test) or sk_live_xxxx (production)" type="password" />
          <Field label="Stripe Publishable Key" value={settings.gateway_public_key}
            onChange={v => updateField('gateway_public_key', v)} hint="pk_test_xxxx (test) or pk_live_xxxx (production)" />
          <div style={{ padding: '8px 12px', background: '#EFF6FF', borderRadius: 6, fontSize: 12, color: '#1E40AF', marginTop: 4 }}>
            To configure Stripe: Go to <a href="https://dashboard.stripe.com/test/apikeys" target="_blank" rel="noopener noreferrer" style={{ color: '#2563EB', textDecoration: 'underline' }}>Stripe Dashboard → API Keys</a>, copy your keys above, then save. Use test keys (sk_test_/pk_test_) for QA.
          </div>
        </Section>
      )}

      {/* Thresholds */}
      <Section title="Thresholds">
        <Field label="Balance Threshold ($)" value={settings.balance_threshold} type="number"
          onChange={v => updateField('balance_threshold', parseFloat(v))} hint="Minimum balance to trigger reminders" />
        <Field label="High Escalation ($)" value={settings.escalation_threshold_high} type="number"
          onChange={v => updateField('escalation_threshold_high', parseFloat(v))} />
        <Field label="Urgent Escalation ($)" value={settings.escalation_threshold_urgent} type="number"
          onChange={v => updateField('escalation_threshold_urgent', parseFloat(v))} />
        <Field label="Collections Threshold (days)" value={settings.collections_threshold_days} type="number"
          onChange={v => updateField('collections_threshold_days', parseInt(v))} />
      </Section>

      {/* Reminder Cadence */}
      <Section title="Reminder Cadence (days)">
        <Field label="Pre-Visit Reminder" value={settings.pre_visit_reminder_days} type="number"
          onChange={v => updateField('pre_visit_reminder_days', parseInt(v))} hint="Days before appointment" />
        <Field label="Post-Visit Reminder" value={settings.post_visit_reminder_days} type="number"
          onChange={v => updateField('post_visit_reminder_days', parseInt(v))} hint="Days after visit" />
        <Field label="1st Follow-up" value={settings.reminder_1_delay} type="number"
          onChange={v => updateField('reminder_1_delay', parseInt(v))} />
        <Field label="2nd Follow-up" value={settings.reminder_2_delay} type="number"
          onChange={v => updateField('reminder_2_delay', parseInt(v))} />
        <Field label="3rd Follow-up" value={settings.reminder_3_delay} type="number"
          onChange={v => updateField('reminder_3_delay', parseInt(v))} />
        <Field label="Final Notice" value={settings.final_notice_delay} type="number"
          onChange={v => updateField('final_notice_delay', parseInt(v))} />
      </Section>

      {/* Channels */}
      <Section title="Channels">
        <ToggleField label="Enable Payment Reminders" value={settings.enable_payment_reminders}
          onChange={v => updateField('enable_payment_reminders', v)} />
        <ToggleField label="SMS Reminders" value={settings.enable_sms_reminders}
          onChange={v => updateField('enable_sms_reminders', v)} />
        <ToggleField label="Email Reminders" value={settings.enable_email_reminders}
          onChange={v => updateField('enable_email_reminders', v)} />
        <ToggleField label="Voice Reminders" value={settings.enable_voice_reminders}
          onChange={v => updateField('enable_voice_reminders', v)} />
        <Field label="Voice Threshold ($)" value={settings.voice_reminder_threshold} type="number"
          onChange={v => updateField('voice_reminder_threshold', parseFloat(v))} hint="Minimum balance for voice calls" />
      </Section>

      {/* EHR Payment Sync */}
      <Section title="EHR Payment Sync">
        <ToggleField label="Enable EHR Payment Sync" value={settings.enable_ehr_payment_sync}
          onChange={v => updateField('enable_ehr_payment_sync', v)} />
        <Field label="Vendor Code" value={settings.ehr_vendor_code || ''} onChange={v => updateField('ehr_vendor_code', v)}
          hint="Patient Pay Vendor code registered with athenahealth" />
        {settings.payment_gateway === 'ELAVON' && (
          <>
            <Field label="Gateway Merchant ID" value={settings.elavon_merchant_id || ''} onChange={v => updateField('elavon_merchant_id', v)}
              hint="From Elavon sandbox/production dashboard" />
            <Field label="Merchant Alias" value={settings.elavon_user_id || ''} onChange={v => updateField('elavon_user_id', v)}
              hint="Used for hosted payment page authentication" />
            <Field label="Secret Key" value={settings.elavon_pin || ''} type="password" onChange={v => updateField('elavon_pin', v)}
              hint="Server-side API authentication — never expose to frontend" />
            <Field label="Public Key" value={settings.elavon_terminal_id || ''} onChange={v => updateField('elavon_terminal_id', v)}
              hint="Safe for frontend — used in HPP Lightbox" />
            <ToggleField label="Elavon via athena CCP" value={settings.elavon_ccp_enabled}
              onChange={v => updateField('elavon_ccp_enabled', v)}
              hint="Turn on if Elavon is provisioned through athena Credit Card Plus. Enables automatic reconciliation in athenaCollector. Leave off for standalone Elavon accounts (our daily reconciliation job will handle it)." />
          </>
        )}
      </Section>

      {/* Card on File & Contracts */}
      <Section title="Card on File & Contracts">
        <ToggleField label="Enable Stored Cards (Card on File)" value={settings.enable_stored_cards}
          onChange={v => updateField('enable_stored_cards', v)} />
        <ToggleField label="Enable One-Year Payment Contracts" value={settings.enable_one_year_contracts}
          onChange={v => updateField('enable_one_year_contracts', v)} />
        <ToggleField label="Enable Auto Check-In" value={settings.enable_auto_checkin}
          onChange={v => updateField('enable_auto_checkin', v)} />
      </Section>

      {/* AI */}
      <Section title="AI Billing Assistant">
        <ToggleField label="Enable AI Billing Assistant" value={settings.enable_ai_billing_assistant}
          onChange={v => updateField('enable_ai_billing_assistant', v)} />
        <ToggleField label="AI Can Provide Balance" value={settings.ai_can_provide_balance}
          onChange={v => updateField('ai_can_provide_balance', v)} />
        <ToggleField label="AI Can Resend Payment Link" value={settings.ai_can_resend_payment_link}
          onChange={v => updateField('ai_can_resend_payment_link', v)} />
        <ToggleField label="AI Can Create Payment Plan" value={settings.ai_can_create_payment_plan}
          onChange={v => updateField('ai_can_create_payment_plan', v)} />
      </Section>

      <div style={{ marginTop: '1.5rem' }}>
        <button onClick={handleSave} disabled={saving}
          style={{ padding: '10px 24px', borderRadius: '6px', border: 'none', background: '#2563EB', color: '#fff', cursor: saving ? 'default' : 'pointer', fontSize: '0.875rem', fontWeight: 500 }}>
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '1.25rem', marginBottom: '1rem' }}>
      <h3 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '1rem', color: '#374151', borderBottom: '1px solid #E5E7EB', paddingBottom: '0.5rem' }}>{title}</h3>
      {children}
    </div>
  );
}

function Field({ label, value, type = 'text', options, onChange, hint }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.75rem' }}>
      <label style={{ width: '200px', fontSize: '0.875rem', color: '#374151' }}>
        {label}
        {hint && <span style={{ display: 'block', fontSize: '0.7rem', color: '#9CA3AF' }}>{hint}</span>}
      </label>
      {type === 'select' ? (
        <select value={value || ''} onChange={e => onChange(e.target.value)}
          style={{ flex: 1, padding: '6px 10px', borderRadius: '6px', border: '1px solid #D1D5DB', fontSize: '0.875rem' }}>
          {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      ) : (
        <input type={type} value={value ?? ''} onChange={e => onChange(e.target.value)}
          style={{ flex: 1, padding: '6px 10px', borderRadius: '6px', border: '1px solid #D1D5DB', fontSize: '0.875rem' }} />
      )}
    </div>
  );
}

function ToggleField({ label, value, onChange }) {
  const isOn = value === 1 || value === true;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.75rem' }}>
      <label style={{ width: '200px', fontSize: '0.875rem', color: '#374151' }}>{label}</label>
      <button onClick={() => onChange(isOn ? 0 : 1)}
        style={{
          width: '44px', height: '24px', borderRadius: '12px', border: 'none', cursor: 'pointer',
          background: isOn ? '#2563EB' : '#D1D5DB', position: 'relative', transition: 'background 0.2s',
        }}>
        <span style={{
          position: 'absolute', top: '2px', left: isOn ? '22px' : '2px',
          width: '20px', height: '20px', borderRadius: '50%', background: '#fff',
          transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        }} />
      </button>
    </div>
  );
}

export default PaymentSettings;
